import { createContext, useContext, useEffect, useRef, useState } from "react";
import keycloak from "@config/keycloak";
import { useAuthStore } from "@persisted-state/auth/auth.store";
import {
  checkPassportOrEmiratesId,
  getMySecurityClearance,
  getSecurityClearanceById,
} from "@services/securityClearanceApi";
import { getUser } from "@services/userApi";
import { useQueryClient } from "@tanstack/react-query";
import {
  completeExternalAuthRoundtrip,
  failExternalAuthRoundtrip,
  failLoginTiming,
  startLoginStage,
} from "@telemetry/loginTiming";
import { startLoginRedirect } from "@telemetry/trackLogin";
import {
  configureUserRolesParticularities,
  isIndividualApplicantUser,
  usesIndividualSecurityClearanceFlow,
} from "@utils/user-permissions";
import { jwtDecode } from "jwt-decode";
import { useLocation, useNavigate } from "react-router-dom";

type KeycloakCtx = { keycloak: typeof keycloak; initialized: boolean };
const Ctx = createContext<KeycloakCtx>({ keycloak, initialized: false });
export const useKeycloakContext = () => useContext(Ctx);
const CHECK_ID_PASSPORT_ONLY_KEY = "checkIdPassportOnly";
const isNotFoundError = (error: unknown) =>
  typeof error === "object" &&
  error !== null &&
  "response" in error &&
  typeof (error as any).response === "object" &&
  (error as any).response !== null &&
  (error as any).response.status === 404;
const normalizeApplicantName = (value?: string | null) => {
  if (!value) return undefined;
  return value
    .trim()
    .split(/\s+/)
    .map((segment) => segment.toUpperCase())
    .join(" ");
};
const normalizeStatus = (value: unknown): string | null => {
  const normalized = String(value ?? "")
    .trim()
    .toUpperCase();
  return normalized || null;
};

export function KeycloakProvider({ children }: { children: React.ReactNode }) {
  const [initialized, setInitialized] = useState(false);
  const qc = useQueryClient();
  const navigate = useNavigate();
  const location = useLocation();

  // Prevent redundant work
  const lastTokenRef = useRef<string | undefined>(undefined);
  const lastFetchedUserIdRef = useRef<string | undefined>(undefined);
  const didRedirectRef = useRef(false);

  const setAuth = useAuthStore.getState().setAuth;
  const setUser = useAuthStore.getState().setUser; // your store handles (user, scId?, scStatus?) in your local impl
  const clearAuth = useAuthStore.getState().clearAuth;
  const clearUser = useAuthStore.getState().clearUser;

  // helpers
  const nextTick = () => Promise.resolve();
  /** Compute where to send the user based on role & SC id/status */
  const computeRedirectPath = (
    user: any,
    scId?: string | null,
    scStatus?: string | null,
  ): string => {
    const perms = user.permissions;
    const roles: string[] = user.roles || [];

    const map = configureUserRolesParticularities(perms, scId ?? null, scStatus ?? null);

    // prefer the first role that yields a config with redirectPath
    for (const role of roles) {
      const conf = (map as any)[role];
      if (conf && conf.redirectPath) return conf.redirectPath as string;
    }

    // fallback route if nothing matched
    return "/";
  };

  const makeAbs = (p: string) => (p && !p.startsWith("/") ? `/${p}` : p || "/");

  const isAuthCallback = (() => {
    const q = location.search || "";
    return q.includes("code=") || q.includes("session_state=");
  })();

  const atRoot = location.pathname === "/" || location.pathname === "/index.html";

  /** After auth & user fetched, perform a one-time redirect */
  const redirectOnce = (targetPath: string) => {
    if (didRedirectRef.current) return;

    const saved = sessionStorage.getItem("postLoginRedirect");

    //  Only redirect:
    //  - right after the Keycloak callback (code/session_state present), OR
    //  - when we are truly at "/"
    if (!(isAuthCallback || atRoot)) return;

    let to: string | null = null;

    if (isAuthCallback) {
      // On auth callback, prefer deep URL if explicitly saved; otherwise use role-based target.
      to = saved || targetPath;
      if (saved) {
        sessionStorage.removeItem("postLoginRedirect");
      }
    } else if (atRoot) {
      // If truly at home, use the role-based target
      to = targetPath;
    } else {
      // Deep URL + no saved redirect → do nothing
      return;
    }

    to = makeAbs(to);
    didRedirectRef.current = true;
    navigate(to, { replace: true });
  };

  /** Fetch current user via TanStack and write to store; fetch SCs for applicant */
  const fetchAndSetUser = async (subject: string) => {
    const userBootstrapStage = startLoginStage("user_bootstrap_total", {
      path: location.pathname,
      trigger: "auth_user_bootstrap",
    });

    // Skip if we already fetched this subject and store matches
    const storeUser = useAuthStore.getState().user;
    const currentId = storeUser?.id ?? undefined;
    if (lastFetchedUserIdRef.current === subject && currentId === subject) {
      userBootstrapStage?.complete();
      return;
    }

    try {
      let user;
      const getUserStage = startLoginStage("get_user", {
        path: location.pathname,
        trigger: "auth_user_bootstrap",
      });
      try {
        user = await qc.fetchQuery({
          queryKey: ["auth", "user"],
          queryFn: getUser,
          staleTime: 5 * 60 * 1000,
          gcTime: 30 * 60 * 1000,
        });
        getUserStage?.complete();
      } catch (getUserError) {
        getUserStage?.fail(getUserError);
        throw getUserError;
      }

      let scId: string | null = null;
      let scStatus: string | null = null;

      const isIndividualUser = isIndividualApplicantUser(user.roles);
      const usesIndividualFlow = usesIndividualSecurityClearanceFlow(user.roles);
      sessionStorage.removeItem(CHECK_ID_PASSPORT_ONLY_KEY);
      if (isIndividualUser) {
        const securityClearanceMeStage = startLoginStage("security_clearance_me", {
          path: location.pathname,
          trigger: "auth_user_bootstrap",
          verificationFlow: "security_clearance_me",
        });
        try {
          const mySecurityClearance = await qc.fetchQuery({
            queryKey: ["security-clearance-me", user.id],
            queryFn: () => getMySecurityClearance({ service: "bff-gatepass" }),
            retry: false,
          });
          securityClearanceMeStage?.complete();
          const myScId = String(
            (
              mySecurityClearance as {
                securityClearanceId?: string;
                securityClearanceRequestId?: string;
                id?: string;
              }
            )?.securityClearanceId ??
              (
                mySecurityClearance as {
                  securityClearanceId?: string;
                  securityClearanceRequestId?: string;
                  id?: string;
                }
              )?.securityClearanceRequestId ??
              (
                mySecurityClearance as {
                  securityClearanceId?: string;
                  securityClearanceRequestId?: string;
                  id?: string;
                }
              )?.id ??
              "",
          ).trim();
          if (myScId) {
            scId = myScId;
            scStatus = normalizeStatus(
              (mySecurityClearance as { status?: string; securityClearanceStatus?: string })
                ?.status ??
                (mySecurityClearance as { status?: string; securityClearanceStatus?: string })
                  ?.securityClearanceStatus,
            );
            if (!scStatus) {
              const securityClearanceDetailsStage = startLoginStage("security_clearance_details", {
                path: location.pathname,
                trigger: "auth_user_bootstrap",
                verificationFlow: "security_clearance_details",
              });
              try {
                const details = await qc.fetchQuery({
                  queryKey: ["security-clearance-details", myScId],
                  queryFn: () => getSecurityClearanceById({ id: myScId, service: "bff-gatepass" }),
                  retry: false,
                });
                securityClearanceDetailsStage?.complete();
                scStatus = normalizeStatus(details?.status);
              } catch (detailsError) {
                securityClearanceDetailsStage?.fail(detailsError);
                if (!isNotFoundError(detailsError)) {
                  console.error("[Keycloak] details lookup for /me SC id failed:", detailsError);
                }
              }
            }
          }
        } catch (myScError) {
          securityClearanceMeStage?.fail(myScError);
          if (!isNotFoundError(myScError)) {
            console.error("[Keycloak] /security-clearance/me lookup error:", myScError);
          }
        }

        if (!scId && usesIndividualFlow) {
          const userEmiratesId = String(user.emiratesId ?? "").trim();
          if (userEmiratesId) {
            const securityClearanceCheckIdStage = startLoginStage("security_clearance_check_id", {
              path: location.pathname,
              trigger: "auth_user_bootstrap",
              verificationFlow: "check_id",
            });
            try {
              const checkResponse = await qc.fetchQuery({
                queryKey: ["security-clearance-check-id", user.id, userEmiratesId],
                queryFn: () =>
                  checkPassportOrEmiratesId({
                    service: "bff-gatepass",
                    emiratesId: userEmiratesId,
                    fullNameEnglish: normalizeApplicantName(user.name),
                  }),
                retry: false,
              });
              securityClearanceCheckIdStage?.complete();

              const identifiedSecurityClearanceId = String(
                (checkResponse as { securityClearanceId?: string })?.securityClearanceId ?? "",
              ).trim();

              if (identifiedSecurityClearanceId) {
                const securityClearanceDetailsStage = startLoginStage(
                  "security_clearance_details",
                  {
                    path: location.pathname,
                    trigger: "auth_user_bootstrap",
                    verificationFlow: "security_clearance_details",
                  },
                );
                let details;
                try {
                  details = await qc.fetchQuery({
                    queryKey: ["security-clearance-details", identifiedSecurityClearanceId],
                    queryFn: () =>
                      getSecurityClearanceById({
                        id: identifiedSecurityClearanceId,
                        service: "bff-gatepass",
                      }),
                    retry: false,
                  });
                  securityClearanceDetailsStage?.complete();
                } catch (detailsError) {
                  securityClearanceDetailsStage?.fail(detailsError);
                  throw detailsError;
                }
                scId = identifiedSecurityClearanceId;
                scStatus = normalizeStatus(details?.status);
              }
            } catch (checkIdError) {
              securityClearanceCheckIdStage?.fail(checkIdError);
              if (!isNotFoundError(checkIdError)) {
                console.error("[Keycloak] check-id lookup error:", checkIdError);
              }
            }
            if (!scId) {
              // Business rule: when an individual has Emirates ID in token but no SC is found,
              // route to Check ID in passport-only mode.
              sessionStorage.setItem(CHECK_ID_PASSPORT_ONLY_KEY, "1");
            }
          }
        }

        setUser(user as any, scId, scStatus, { updateBootstrap: true });
      } else {
        setUser(user as any, null, null, { updateBootstrap: true });
      }

      // wait a microtask so subscribers/UI have reacted to store update
      await nextTick();

      // compute redirect from what's actually in the store (or fall back to local values)
      const st = useAuthStore.getState();
      const target = computeRedirectPath(
        st.user ?? user,
        (st as any).securityClearanceId ?? scId,
        (st as any).securityClearanceStatus ?? scStatus,
      );

      const finalRedirectStage = startLoginStage("final_redirect", {
        path: location.pathname,
        trigger: "auth_user_bootstrap",
      });
      try {
        redirectOnce(target);
        finalRedirectStage?.complete();
      } catch (redirectError) {
        finalRedirectStage?.fail(redirectError);
        throw redirectError;
      }

      lastFetchedUserIdRef.current = (user as any)?.id ?? subject;
      userBootstrapStage?.complete();
    } catch (e) {
      userBootstrapStage?.fail(e);
      console.error("[Keycloak] getUser failed:", e);
      failLoginTiming(e, {
        path: location.pathname,
        trigger: "auth_user_bootstrap",
      });
      // optionally: clearAuth(); clearUser();
    }
  };

  /** Persist token+sub → store, then fetch user (and redirect) */
  const writeTokenToStore = async (token?: string) => {
    if (!token || token === lastTokenRef.current) return;
    lastTokenRef.current = token;

    const tokenProcessingStage = startLoginStage("token_processing", {
      path: location.pathname,
      trigger: "auth_token_processing",
    });
    try {
      const { sub } = jwtDecode<{ sub: string }>(token);
      setAuth(token, sub);
      tokenProcessingStage?.complete();
      await fetchAndSetUser(sub);
    } catch (e) {
      tokenProcessingStage?.fail(e);
      console.error("[Keycloak] jwt decode failed:", e);
      failLoginTiming(e, {
        path: location.pathname,
        trigger: "auth_token_decode",
      });
    }
  };

  const clearAllFromStore = () => {
    lastTokenRef.current = undefined;
    lastFetchedUserIdRef.current = undefined;
    didRedirectRef.current = false;
    clearUser();
    clearAuth();
    qc.removeQueries({ queryKey: ["auth", "user"] });
  };

  // --- lifecycle ------------------------------------------------------------

  useEffect(() => {
    let cancelled = false;
    globalThis.performance?.mark?.("keycloak-init-start");
    const keycloakInitStage = startLoginStage("keycloak_init", {
      path: location.pathname,
      trigger: "keycloak_init",
    });
    keycloak
      .init({
        onLoad: "check-sso",
        silentCheckSsoRedirectUri: `${window.location.origin}/silent-check-sso.html`,
        pkceMethod: "S256",
        checkLoginIframe: false,
      })
      .then(async (authenticated) => {
        keycloakInitStage?.complete();
        globalThis.performance?.mark?.("keycloak-init-resolved");
        if (cancelled) return;
        if (authenticated && keycloak.token) {
          completeExternalAuthRoundtrip();
          await writeTokenToStore(keycloak.token);
        } else {
          failExternalAuthRoundtrip(
            new Error("Keycloak initialization completed without authentication"),
          );
          failLoginTiming(new Error("Keycloak initialization completed without authentication"), {
            path: location.pathname,
            trigger: "keycloak_init_unauthenticated",
          });
          clearAllFromStore();
        }
        globalThis.performance?.mark?.("keycloak-before-set-initialized");
        setInitialized(true);
      })
      .catch((err) => {
        keycloakInitStage?.fail(err);
        console.error("[Keycloak] init error", err);
        failExternalAuthRoundtrip(err);
        failLoginTiming(err, {
          path: location.pathname,
          trigger: "keycloak_init_error",
        });
        setInitialized(true);
      });

    // Keycloak events
    keycloak.onAuthSuccess = async () => {
      await writeTokenToStore(keycloak.token);
    };

    keycloak.onAuthError = (err) => {
      console.error("[Keycloak] auth error", err);
      failExternalAuthRoundtrip(err);
      failLoginTiming(err, {
        path: location.pathname,
        trigger: "keycloak_auth_error",
      });
    };

    keycloak.onAuthRefreshSuccess = async () => {
      await writeTokenToStore(keycloak.token);
      // Optional: refetch auth-scoped queries on refresh
      qc.invalidateQueries({ predicate: (q) => q.meta?.auth === true });
    };

    keycloak.onAuthRefreshError = () => {
      console.warn("[Keycloak] token refresh error — forcing login");
      clearAllFromStore();
      startLoginRedirect(
        {
          path: location.pathname,
          trigger: "auth_refresh_error",
        },
        () => keycloak.login(),
      );
    };

    keycloak.onTokenExpired = () => {
      keycloak
        .updateToken(30)
        .then(async (refreshed) => {
          if (refreshed) await writeTokenToStore(keycloak.token);
          else {
            clearAllFromStore();
            startLoginRedirect(
              {
                path: location.pathname,
                trigger: "token_expired_not_refreshed",
              },
              () => keycloak.login(),
            );
          }
        })
        .catch(() => {
          clearAllFromStore();
          startLoginRedirect(
            {
              path: location.pathname,
              trigger: "token_expired_refresh_failed",
            },
            () => keycloak.login(),
          );
        });
    };

    keycloak.onAuthLogout = () => {
      clearAllFromStore();
    };

    return () => {
      cancelled = true;
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <Ctx.Provider value={{ keycloak, initialized }}>{children}</Ctx.Provider>;
}
